import { useLibrary } from "../contexts/LibraryContext";
import { lazy, Suspense, useEffect, useState } from "react";
import { getReading, type ReadingContent } from "../api";
const PdfReader = lazy(() => import("./PdfReader"));
export function Reader({ slug, pdf }: { slug: string; pdf?: string }) {
  if (pdf)
    return (
      <Suspense fallback={<p role="status">Opening document…</p>}>
        <PdfReader key={pdf} slug={slug} pdf={pdf} />
      </Suspense>
    );
  return <TextReader slug={slug} />;
}
function TextReader({ slug }: { slug: string }) {
  const library = useLibrary();
  const [data, setData] = useState<ReadingContent | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [size, setSize] = useState(20);
  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    const linked = window.location.hash.match(/^#passage-(\d+)$/);
    getReading(slug, null, linked ? Number(linked[1]) : undefined)
      .then((d) => {
        if (live) {
          setData(d);
          if (linked)
            setTimeout(
              () =>
                document
                  .getElementById(`passage-${linked[1]}`)
                  ?.scrollIntoView(),
              0,
            );
        }
      })
      .catch(() => {
        if (live)
          setError(
            "The reading could not be loaded. You can still open an available source file below.",
          );
      });
    return () => {
      live = false;
    };
  }, [slug]);
  if (!data?.segments.length && !error) return null;
  return (
    <section className="integrated-reader" aria-label="Read this work">
      <header>
        <h2>Settle in and read.</h2>
        <label>
          Text size
          <select
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
          >
            <option value="18">Small</option>
            <option value="20">Medium</option>
            <option value="24">Large</option>
          </select>
        </label>
      </header>
      {library.reading[slug] != null ? (
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const next = await getReading(slug, null, library.reading[slug]);
              setData(next);
              window.location.hash = `passage-${library.reading[slug]}`;
              setTimeout(
                () =>
                  document
                    .getElementById(`passage-${library.reading[slug]}`)
                    ?.scrollIntoView(),
                0,
              );
            } catch {
              setError(
                "Could not return to your saved place. Please try again.",
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          Return to your saved place
        </button>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}
      {data?.segments.length ? (
        <div className="reader-text" style={{ fontSize: size }}>
          {data.segments.map((s) => (
            <section key={s.id} id={`passage-${s.position}`}>
              <p>{s.text}</p>
              <a href={`#passage-${s.position}`}>Link to this passage</a>
              <button
                className="reading-bookmark"
                onClick={() => library.recordReading(slug, s.position)}
              >
                {library.reading[slug] === s.position
                  ? "Place saved"
                  : "Save my place"}
              </button>
            </section>
          ))}
          {data.nextCursor ? (
            <button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const next = await getReading(slug, data.nextCursor);
                  setData({
                    ...next,
                    segments: [...data.segments, ...next.segments],
                  });
                } catch {
                  setError(
                    "The next section could not be loaded. Please try again.",
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Loading…" : "Continue reading"}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
